import { prop, propEq, reduce, reject } from 'ramda';
import { XRegExp } from '../../../../xregexp-min';
import { paramResultDs, paramResultPath } from '../constants/parameterNames';
import { typeNumber } from '../constants/parameterTypes';
import { findParameter } from './parameter.accessors';
import { getNamedGroups } from './qRules.string.helpers';

function getFormattedCommandParameter(key, details) {
    return {
        name: key,
        description: details.description,
        type: details.type,
        value: details.value || (details.type === typeNumber ? 0 : ''),
        dsParentName: details.dsParentName || '',
        options: details.options || [],
        minValue: details.minValue || 0,
    };
}

/*
 * If the provided value is a key-value object of parameter definitions, converts them to an array of parameter objects
 */
function convertParameters(parameters) {
    if (Array.isArray(parameters)) {
        return parameters;
    }

    return Object.keys(parameters).map(function (key) {
        var details = parameters[key];

        return typeof details === 'string'
            ? { name: key, description: details }
            : getFormattedCommandParameter(key, details);
    });
}

const getParameterNames = (parameters) =>
    Array.isArray(parameters)
        ? parameters.map(prop('name'))
        : Object.keys(parameters);

const buildValidationParameters = (paramSet, required) =>
    getParameterNames(paramSet).map((name) => ({ name, required }));

const getCommandParameters = (optionalParameters, requiredParameters) => [
    ...buildValidationParameters(optionalParameters, false),
    ...buildValidationParameters(requiredParameters, true),
    { name: paramResultDs },
    { name: paramResultPath },
];

const removeParameterSingle = (parameterSet, name) =>
    reject(propEq('Key', name), parameterSet);

function removeParameter(parameterSet, parameter) {
    const nameCandidates = [
        parameter.Name,
        parameter.Name + '-xpath',
        parameter.Name + '-dsname',
    ];

    return reduce(removeParameterSingle, parameterSet, nameCandidates);
}

function validateParameters(parameterCollection, validParameters, usageText) {
    let testParameters = parameterCollection.parameterArray.slice();

    for (const { name, required } of validParameters) {
        // Identify whether param exists in dictionary.
        const parameter = findParameter(parameterCollection, name);

        if (parameter) {
            // Remove parameter for later tests.
            testParameters = removeParameter(testParameters, parameter);
        }

        // Validate against existence requirements.
        // We allow specifying blank parameters now, so a parameter's value must
        // be non-empty to qualify satisfy the required requirement.
        // In the future, we may want a way of indicating parameters that are required
        // but can be blank.
        const isSpecified = parameter && (parameter.Value || parameter.XPath);

        if (required && !isSpecified) {
            throw new Error(
                `Required parameter unspecified: ${name}.\n\n${usageText}`,
            );
        }
    }

    // Check for extraneous params.
    if (testParameters.length !== 0) {
        const keyList = testParameters.map(prop('Key')).join();

        throw new Error(`Unexpected parameter(s): ${keyList}.\n\n${usageText}`);
    }

    return true;
}

const makeParameterCollection = (parameterArray, dataSources) => ({
    dataSources,
    parameterArray,
});

function getPositionArray(params) {
    let positionArray = [];

    XRegExp.forEach(
        params,
        /(\s\/[a-z0-9]+(?:-xpath|-dsname)?=)/i,
        function (match) {
            positionArray.push(match.index);
        },
    );

    return positionArray;
}

const paramGroupToParam = (paramGroup) => ({
    Key: paramGroup['Key'],
    Value: paramGroup['Value'],
});

const getParamFromString = (positionArray, params, paramPosition, index) => {
    const paramString =
        index === positionArray.length - 1
            ? params.substr(paramPosition)
            : params.substr(
                  paramPosition,
                  positionArray[index + 1] - positionArray[index],
              );

    const paramGroup = getNamedGroups(
        '^\\s/(?<Key>[a-z0-9]+(?:-xpath|-dsname)?)=(?<Value>.*)$',
        paramString,
    );

    if (paramGroup && paramGroup.length === 3) {
        return { param: paramGroupToParam(paramGroup) };
    }

    return {
        paramError: `Key, Value not found for param: ${paramString} `,
    };
};

const getParamsArray = (params, positionArray) =>
    positionArray.reduce(
        ({ error, data }, paramPosition, index) => {
            const { param, paramError } = getParamFromString(
                positionArray,
                params,
                paramPosition,
                index,
            );

            if (paramError) {
                return {
                    data,
                    error: error + paramError,
                };
            }

            return {
                data: [...data, param],
                error,
            };
        },
        { error: '', data: [] },
    );

export const getValidParamArray = (namedGroups) => {
    if (!namedGroups) {
        return {
            error: 'Command format is invalid.',
        };
    }

    const params = namedGroups['Params'];
    const positionArray = getPositionArray(params);
    const paramsArray = getParamsArray(params, positionArray);
    const paramsArrayResult = paramsArray.data;

    if (paramsArrayResult.length !== positionArray.length) {
        return {
            error: paramsArray.error,
        };
    }

    return {
        result: paramsArrayResult,
        success: true,
    };
};

export const processCommandString = (commandString, dataSources) => {
    const namedGroups = getNamedGroups(
        '^(?<Command>[a-zA-Z0-9]+)(?<Params>.+)?$',
        commandString,
    );
    const paramsArrayResult = getValidParamArray(namedGroups);

    if (!paramsArrayResult.success) {
        return { success: false, error: paramsArrayResult.error };
    }

    const parameters = makeParameterCollection(
        paramsArrayResult.result,
        dataSources,
    );

    const commandName = namedGroups['Command'];

    return {
        success: true,
        commandName,
        parameters,
    };
};

export {
    convertParameters,
    getCommandParameters,
    makeParameterCollection,
    validateParameters,
};
